import { type TJsonEditorType, type TJsonPath } from './jsonField.types';

const MAX_JSON_RENDER_DEPTH = 50;

export function parseJsonObjectString(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function renderPrimitiveJsonValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return JSON.stringify(value);
  }
  if (value === null) {
    return JSON.stringify(value);
  }
  return String(value);
}

export function renderDenseJson(node: unknown, depth = 0): string {
  if (depth >= MAX_JSON_RENDER_DEPTH) {
    return '...';
  }

  const indent = ' '.repeat(depth * 2);
  const childIndent = ' '.repeat((depth + 1) * 2);

  if (isPlainObject(node)) {
    const entries = Object.entries(node);
    if (!entries.length) {
      return '{}';
    }

    const content = entries
      .map(
        ([key, value]) =>
          `${childIndent}${JSON.stringify(key)}: ${renderDenseJson(value, depth + 1)}`
      )
      .join(',\n');

    return `{\n${content}\n${indent}}`;
  }

  if (Array.isArray(node)) {
    if (!node.length) {
      return '[]';
    }

    const content = node
      .map((item) => `${childIndent}${renderDenseJson(item, depth + 1)}`)
      .join(',\n');

    return `[\n${content}\n${indent}]`;
  }

  return renderPrimitiveJsonValue(node);
}

export function inferJsonFieldType(value: unknown): TJsonEditorType {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'string') {
    return 'string';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  return 'json';
}

export function pathToKey(path: TJsonPath): string {
  return JSON.stringify(path);
}

export function getAtPath(root: unknown, path: TJsonPath): unknown {
  return path.reduce((current, segment) => {
    if (Array.isArray(current) && typeof segment === 'number') {
      return current[segment];
    }
    if (isPlainObject(current) && typeof segment === 'string') {
      return current[segment];
    }
    return undefined;
  }, root);
}

export function setAtPath(root: unknown, path: TJsonPath, value: unknown): unknown {
  if (!path.length) {
    return value;
  }
  const [segment, ...rest] = path;

  if (Array.isArray(root) && typeof segment === 'number') {
    const nextArray = [...root];
    nextArray[segment] = setAtPath(nextArray[segment], rest, value);
    return nextArray;
  }

  if (isPlainObject(root) && typeof segment === 'string') {
    return {
      ...root,
      [segment]: setAtPath(root[segment], rest, value),
    };
  }

  return root;
}

export function parseEditedJsonValue(
  input: string,
  previousValue: unknown
): { value?: unknown; error?: string } {
  const trimmed = input.trim();
  if (!trimmed) {
    if (typeof previousValue === 'string') {
      return { value: '' };
    }

    return { error: 'Value cannot be empty.' };
  }

  if (typeof previousValue === 'string') {
    return { value: input };
  }

  try {
    return { value: JSON.parse(trimmed) };
  } catch {
    if (typeof previousValue === 'boolean') {
      return { value: input };
    }

    return { error: 'Invalid JSON value.' };
  }
}
